import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FaRegFileAlt } from 'react-icons/fa';

const popIn = {
    hidden: { opacity: 0, scale: 0.6, x: 40 },
    visible: {
        opacity: 1,
        scale: 1,
        x: 0,
        transition: {
            type: 'spring',
            stiffness: 260,
            damping: 22,
        },
    },
};

const termsContent = [
    {
        title: '1. Reservations',
        items: [
            'All reservations are subject to availability.',
            'A reservation is confirmed only after receipt of the required advance payment and written confirmation from Mahi Beach Ayurveda Resort.',
            'Guests must provide accurate personal information when making a reservation.',
        ],
    },
    {
        title: '2. Deposit & Payment',
        items: [
            'A deposit of €500 is required to confirm the reservation.',
            'The remaining balance must be paid upon arrival at the resort unless otherwise agreed in writing.',
            'Payments can be made via bank transfer or other approved payment methods.',
        ],
    },
    {
        title: '3. Cancellation Policy',
        items: [
            'The reservation deposit is non-refundable.',
            'If a cancellation is made at least 30 days before arrival, the deposit may be transferred to a future stay within 12 months, subject to availability.',
            'Cancellations made less than 30 days before arrival, no-shows, or early departures are non-refundable.',
        ],
    },
    {
        title: '4. Check-in & Check-out',
        items: [
            'Check-in: 2:00 PM',
            'Check-out: 11:00 AM',
            'Early check-in and late check-out are subject to availability and may incur additional charges.',
        ],
    },
    {
        title: '5. Ayurveda Treatments',
        items: [
            'All treatment programmes begin with a consultation by our qualified Ayurveda doctor.',
            'Treatment plans are individually tailored and may be adjusted during the stay based on the doctor\'s assessment.',
            'Guests are expected to follow the doctor\'s advice, including dietary recommendations and treatment schedules, for the best results.',
        ],
    },
    {
        title: '6. Health Information',
        items: [
            'Guests must disclose any existing medical conditions, allergies, medications, or pregnancy before arrival.',
            'Failure to provide accurate medical information may affect the suitability or effectiveness of treatments.',
        ],
    },
    {
        title: '7. Meals',
        items: [
            'Ayurveda packages include meals prepared according to the doctor\'s recommendations.',
            'Special dietary requirements should be communicated before arrival.',
        ],
    },
    {
        title: '8. Airport Transfers',
        items: [
            'Airport transfers can be arranged upon request at an additional cost.',
            'Flight details must be provided at least 72 hours before arrival.',
        ],
    },
    {
        title: '9. Guest Conduct',
        items: [
            'Guests are expected to respect other guests, staff, and resort property.',
            'Smoking is permitted only in designated outdoor areas.',
            'Illegal substances and disruptive behaviour are strictly prohibited.',
        ],
    },
    {
        title: '10. Damage to Property',
        items: [
            'Guests are responsible for any damage caused to resort property during their stay.',
            'Repair or replacement costs may be charged accordingly.',
        ],
    },
    {
        title: '11. Personal Belongings',
        items: [
            'The resort is not responsible for the loss, theft, or damage of personal belongings.',
            'Guests are encouraged to use the in-room safe for valuables.',
        ],
    },
    {
        title: '12. Force Majeure',
        text: 'Mahi Beach Ayurveda Resort shall not be liable for cancellations, delays, or interruptions caused by events beyond its reasonable control, including natural disasters, pandemics, government restrictions, or transportation disruptions.',
    },
    {
        title: '13. Liability',
        text: 'While every effort is made to ensure a safe and comfortable stay, guests participate in treatments, yoga, excursions, and other activities at their own risk. The resort\'s liability is limited to the maximum extent permitted by applicable law.',
    },
    {
        title: '14. Privacy',
        text: 'Personal information provided by guests is used solely for reservation, treatment, and operational purposes and will be handled confidentially in accordance with applicable data protection laws.',
    },
    {
        title: '15. Acceptance',
        text: 'By confirming a reservation and paying the required deposit, guests acknowledge that they have read, understood, and agreed to these Terms & Conditions.',
    },
];

const TermsandConditions = () => {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [open]);

    return (
        <>
            <motion.button
                onClick={() => setOpen(true)}
                initial="hidden"
                animate="visible"
                variants={popIn}
                aria-label="Terms and Conditions"
                className="fixed right-4 sm:right-6 md:right-8 top-[60%] z-50"
            >
                <motion.div
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.96 }}
                    className="relative flex items-center gap-3 rounded-full px-4 py-3 sm:px-5 sm:py-3.5 bg-white/95 text-[#1A2F2A] shadow-2xl border border-[#E8C07D]/30"
                >
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#E8C07D] text-[#1A2F2A] shadow-md">
                        <FaRegFileAlt className="h-6 w-6" />
                    </span>
                    <span className="hidden sm:block text-sm sm:text-base font-medium tracking-wide">
                        Terms & Conditions
                    </span>
                </motion.div>
            </motion.button>

            {open && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4 py-6">
                    <div
                        className="absolute inset-0 bg-black/60"
                        onClick={() => setOpen(false)}
                    />
                    <div className="relative z-[10000] max-w-3xl w-full max-h-[90vh] overflow-auto rounded-3xl bg-white shadow-2xl ring-1 ring-black/10 p-5 sm:p-7">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5">
                            <h2 className="text-xl md:text-2xl font-semibold">Mahi Beach Ayurveda Resort – Terms & Conditions</h2>
                            <button
                                onClick={() => setOpen(false)}
                                className="text-gray-600 hover:text-gray-900 text-2xl leading-none"
                                aria-label="Close terms"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="space-y-5 text-gray-800">
                            {termsContent.map((section) => (
                                <div key={section.title}>
                                    <h3 className="text-base font-semibold text-gray-900">{section.title}</h3>
                                    {section.items ? (
                                        <ul className="mt-2 list-disc list-inside space-y-2 text-sm sm:text-base">
                                            {section.items.map((item) => (
                                                <li key={item}>{item}</li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="mt-2 text-sm sm:text-base leading-relaxed">{section.text}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default TermsandConditions;
